import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, finalize, takeUntil } from 'rxjs';
import { DataModalComponent } from '../../../../shared/components/wi-data-modal/wi-data-modal.component';
import { extractErrorMessage } from '../../../../shared/utils/error.util';
import {
  CreateRewashOrderPayload,
  CreateRewashOrderResult,
  RewashReasonPreset,
  RewashReasonService,
} from '../../../../services/rewash-reason.service';
import { OrderRequestRewashTarget } from '../../models/notification-order-request.models';
import { normalizeRewashTarget } from '../../utils/notification-order-request-history.util';

export const REWASH_REASON_OTHERS_VALUE = '__others__';

@Component({
  selector: 'app-rewash-reason-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, DataModalComponent],
  templateUrl: './rewash-reason-modal.component.html',
  styleUrls: ['./rewash-reason-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class RewashReasonModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() originalOrderId: string | null = null;
  @Input() originalSubscriptionWashId: string | null = null;
  /** CWS-1882: a prepaid visit is rewashed by its `prepaid_redemptions` row id. */
  @Input() originalPrepaidRedemptionId: string | null = null;
  /** Lane the camera event resolved; unset for a staff-initiated rewash. */
  @Input() laneId: string | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<CreateRewashOrderResult>();

  readonly othersValue = REWASH_REASON_OTHERS_VALUE;

  presets: RewashReasonPreset[] = [];
  customReasonMaxLength = 100;
  selectedReason: string | null = null;
  customReasonText = '';

  isLoading = false;
  isSubmitting = false;
  loadError = '';
  submitError = '';

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly rewashReasonService: RewashReasonService) {}

  get isOthersSelected(): boolean {
    return this.selectedReason === this.othersValue;
  }

  get hasRewashTarget(): boolean {
    return normalizeRewashTarget(this.rewashTarget) !== null;
  }

  /** The three mutually exclusive ids this modal was opened with. */
  private get rewashTarget(): OrderRequestRewashTarget {
    return {
      originalOrderId: this.originalOrderId?.trim() || undefined,
      originalSubscriptionWashId: this.originalSubscriptionWashId?.trim() || undefined,
      originalPrepaidRedemptionId: this.originalPrepaidRedemptionId?.trim() || undefined,
    };
  }

  get canConfirm(): boolean {
    if (this.isSubmitting || !this.hasRewashTarget || !this.selectedReason) {
      return false;
    }
    if (this.isOthersSelected) {
      return this.customReasonText.trim().length > 0;
    }
    return true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.resetForm();
      this.loadPresets();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    if (this.isSubmitting) {
      return;
    }
    this.closed.emit();
  }

  onConfirm(): void {
    if (!this.canConfirm || !this.hasRewashTarget) {
      return;
    }

    const rewashReason = this.isOthersSelected
      ? this.customReasonText.trim()
      : (this.selectedReason as string);

    this.submitError = '';
    this.isSubmitting = true;

    const laneId = this.laneId?.trim() || '';
    const target = normalizeRewashTarget(this.rewashTarget);
    const payload: CreateRewashOrderPayload = {
      rewash_reason: rewashReason,
      ...(target?.originalOrderId ? { original_order_id: target.originalOrderId } : {}),
      ...(target?.originalSubscriptionWashId
        ? { original_subscription_wash_id: target.originalSubscriptionWashId }
        : {}),
      ...(target?.originalPrepaidRedemptionId
        ? { original_prepaid_redemption_id: target.originalPrepaidRedemptionId }
        : {}),
      ...(laneId ? { lane_id: laneId } : {}),
    };

    this.rewashReasonService
      .createRewashOrder(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: response => {
          if (!response?.status || !response.data) {
            this.submitError = response?.message || 'Could not create rewash order.';
            return;
          }
          this.confirmed.emit(response.data);
        },
        error: error => {
          this.submitError = extractErrorMessage(error, 'Could not create rewash order.');
        },
      });
  }

  private resetForm(): void {
    this.selectedReason = null;
    this.customReasonText = '';
    this.loadError = '';
    this.submitError = '';
    this.isSubmitting = false;
  }

  private loadPresets(): void {
    this.isLoading = true;
    this.rewashReasonService
      .getRewashReasonPresets()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isLoading = false;
          if (!response?.status || !Array.isArray(response.data)) {
            this.loadError = response?.message || 'Could not load rewash reasons.';
            this.presets = [];
            return;
          }
          this.presets = response.data;
          const maxLen = Number(response.custom_reason_max_length);
          this.customReasonMaxLength = Number.isFinite(maxLen) && maxLen > 0 ? maxLen : 100;
        },
        error: () => {
          this.isLoading = false;
          this.loadError = 'Could not load rewash reasons.';
          this.presets = [];
        },
      });
  }
}
